import { usePermissionsContext } from '@carboncopyco/components';
import { LibraryOutlineIcon, SettingsIcon } from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Avatar,
  ColorHex,
  Logo,
  PrideLogo,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useClickOutside } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { rendererLogger } from '../../app/Logger';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useRestart } from '../../hooks/useRestart';
import { HOME_TAB_ID } from '../../services/TabManager';
import { openLinkInBrowser } from '../../shared/utils';
import { useTabs, useUser } from '../../store/context';
import UserMenu from '../Dropdown/UserMenu';
import OrganizationAutocompleteModal from '../OrganizationAutocomplete';
import { WelcomeScreenButton } from '../Workspaces/WorkspacesSidebar/WelcomeScreenButton';

const logger = rendererLogger('Navigation');

type NavigationProps = {
  children?: JSX.Element;
  disabledUserMenu?: boolean;
  showDashboardButton?: boolean;
};

const Navigation = ({
  disabledUserMenu = false,
  showDashboardButton = false,
  children,
}: NavigationProps) => {
  const [isUserMenuVisible, setIsUserMenuVisible] = useState<boolean>(false);
  const [isOrganizationModalVisible, setIsOrganizationModalVisible] =
    useState(false);
  const { setActiveGlobalTabId } = useTabs();
  const { navigateToHomepage, navigateToWebSettings } = useCustomNavigate();
  const flags = useFlagsFallback();
  const { restartApp } = useRestart();

  const { activeOrganization, setActiveOrganization, setMe, me } = useUser();

  const handleOpenOrganizationsModal = () => {
    setIsUserMenuVisible(false);
    setIsOrganizationModalVisible(!isOrganizationModalVisible);
  };

  const handleLogout = () => {
    setIsUserMenuVisible(false);
    window.dsDesktop.user.logOut();
  };

  const dropdownOrganizationRef = useClickOutside(() => {
    setIsOrganizationModalVisible(false);
  });

  const handleTriggerUserMenuModal = () => {
    setIsUserMenuVisible(!isUserMenuVisible);
  };

  const setActiveOrg = async (org: any) => {
    logger.info('Setting active organization', {
      orgId: org?.id,
    });
    await window.dsDesktop.user.updateSelectedOrganization(org);
    setActiveOrganization(org);
    restartApp();
  };

  const handleComponentMount = async () => {
    const storageMe = await window?.dsDesktop?.me();
    setMe(storageMe);
  };

  useEffect(() => {
    handleComponentMount();
  }, []);

  const { getPermissions } = usePermissionsContext();

  const updateRolePermission = getPermissions(
    PermissionKey.roles,
    PermissionAction.updateRoleById
  );

  return (
    <div className="relative z-10 flex bg-ds-accent3-100 flex-shrink-0 border-b h-ds-15">
      {showDashboardButton ? (
        <div
          className="w-ds-15 border-b h-ds-15 border-r flex justify-center items-center self-stretch"
          data-testid="dashboardButtonWrapper"
        >
          <WelcomeScreenButton
            isSelected={false}
            onClick={() => {
              setActiveGlobalTabId(HOME_TAB_ID);
              navigateToHomepage();
            }}
          />
        </div>
      ) : null}
      <div className="flex flex-1 justify-between items-center px-9 h-full">
        <Link to={`/${activeOrganization?.id ?? ''}`}>
          {flags?.platformPrideLogo ? <PrideLogo /> : <Logo />}
        </Link>
        {children}
        <div className="flex items-center justify-center gap-3">
          <TooltipBubble
            placement="right"
            title="Knowledge Base"
          >
            <div
              role="button"
              className="cursor-pointer"
              onClick={() =>
                openLinkInBrowser(null, 'https://help.demostack.com/articles')
              }
              data-ds-id="knowledge-base"
            >
              <LibraryOutlineIcon
                size={20}
                color={ColorHex.natural600}
              />
            </div>
          </TooltipBubble>

          {updateRolePermission && (
            <TooltipBubble
              placement="right"
              title="Organization settings"
            >
              <div
                className={'hover:cursor-pointer'}
                onClick={navigateToWebSettings}
                data-ds-id="organization-settings"
              >
                <SettingsIcon size={20} />
              </div>
            </TooltipBubble>
          )}

          {disabledUserMenu ? (
            <div>
              {activeOrganization && (
                <TooltipBubble
                  placement="left"
                  title={activeOrganization?.organization_name}
                >
                  <Avatar
                    initials={activeOrganization?.organization_name[0]}
                    image={activeOrganization?.icon}
                  />
                </TooltipBubble>
              )}
            </div>
          ) : (
            <UserMenu
              userEmail={me?.email}
              triggerElement={
                <div className="h-ds-7.5 ml-auto">
                  {activeOrganization && (
                    <div
                      className="flex justify-end cursor-pointer items-center"
                      onClick={handleTriggerUserMenuModal}
                      data-testid="organizationAvatar"
                    >
                      {activeOrganization && (
                        <TooltipBubble
                          placement="left"
                          title={activeOrganization?.organization_name}
                        >
                          <Avatar
                            initials={activeOrganization?.organization_name[0]}
                            image={activeOrganization?.icon}
                          />
                        </TooltipBubble>
                      )}
                    </div>
                  )}
                </div>
              }
              organization={activeOrganization}
              openOrganizationModal={handleOpenOrganizationsModal}
              onLogout={handleLogout}
            />
          )}
        </div>
      </div>
      <div ref={dropdownOrganizationRef}>
        {isOrganizationModalVisible && (
          <OrganizationAutocompleteModal
            triggerModal={handleOpenOrganizationsModal}
            visible={isOrganizationModalVisible}
            setActiveOrganization={setActiveOrg}
          />
        )}
      </div>
    </div>
  );
};

export default Navigation;
